import { useQuery } from 'react-query';

import _ from 'lodash';

import { baseApiUrl, fetcher, ServerErrorResponse, createParamString } from 'data_sources/constants';
import datetimeHelper from 'helpers/datetimeHelper';
import { generateRiskPoolMetrics } from './riskPoolMetricsGenerator';
import { useRiskPoolDemoData } from './useRiskPoolDemoData';

import type { ComplianceOverviewSummary } from '../../_types/risk_pool';

interface QueryParams {
  startDate: string;
  endDate: string;
  search?: string;
  compliance?: string;
  policyMods?: string;
}

// Generate mock data from organizations for demo purposes
const generateMockData = (organizations: any[]): ComplianceOverviewSummary => {
  return {
    meetsAll: organizations.filter((org) => org.compliancePerformance === 'meetsAll').length,
    meetsMajority: organizations.filter((org) => org.compliancePerformance === 'meetsMajority').length,
    meetsSome: organizations.filter((org) => org.compliancePerformance === 'meetsSome').length,
    meetsNone: organizations.filter((org) => org.compliancePerformance === 'meetsNone').length,
    total: organizations.length,
  };
};

export const getComplianceOverview = async (
  orgId: string,
  params: QueryParams,
  signal?: AbortSignal,
  onError?: (errors: ServerErrorResponse) => void
): Promise<ComplianceOverviewSummary> => {
  const url = `${baseApiUrl()}/activity-center/v1/organizations/${orgId}/risk-pool/policy/compliance-overview`;

  const apiParams = {
    startDate: params.startDate,
    endDate: params.endDate,
    search: params.search,
    compliance: params.compliance,
    policyMods: params.policyMods,
  };

  const paramString = createParamString(apiParams);

  try {
    const apiData = await fetcher({
      method: 'get',
      url: `${url}?${paramString}`,
      options: { signal },
    });
    return apiData;
  } catch (error: unknown) {
    if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
      onError(error as ServerErrorResponse);
    }
    throw error;
  }
};

export default function useComplianceOverview(
  orgId: string,
  params: QueryParams,
  options = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  // Validate dates before enabling the query
  const areDatesValid = datetimeHelper.isValidDateString(params.startDate) && datetimeHelper.isValidDateString(params.endDate);
  const isDemoDataEnabled = useRiskPoolDemoData();

  return useQuery<ComplianceOverviewSummary, Error>(
    ['compliance_overview', orgId, params.startDate, params.endDate, params.search, params.compliance, params.policyMods],
    async ({ signal }): Promise<ComplianceOverviewSummary> => {
      // If demo data is enabled, return mock data instead of calling API
      if (isDemoDataEnabled) {
        // Generate mock organizations and calculate from them
        const organizations = generateRiskPoolMetrics(orgId, params);
        return generateMockData(organizations);
      }
      return getComplianceOverview(orgId, params, signal, onError);
    },
    {
      ...options,
      enabled: !!orgId && areDatesValid,
      keepPreviousData: true,
      refetchOnWindowFocus: true,
      refetchInterval: isDemoDataEnabled ? false : 1000 * 60 * 2, // 2 minutes, disabled for demo data
    }
  );
}
